import React, { useCallback } from "react";

import { Command } from "cmdk";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Button from "ds/Button";
import getUrl from "Libs/getUrl";
import { selectCommandMenuEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { useAppSelector } from "Store/hooks";

import { ResourcesList } from "./components/ResourcesList";
import { SelectedResourceList } from "./components/SelectedResourceList";
import { useCommandMenuContext } from "./hooks";
import * as S from "./styles";

const selectedResourceLabelMap = {
  projects: "project",
  organizations: "organization"
};

function CommandK() {
  const intl = useIntl();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const { setIsCommandMenuOpen, inputValue, setInputValue } =
    useCommandMenuContext();
  const { projectId, organizationId: organizationName } = useParams();
  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationName
    })
  );
  const organizationId = organization?.id;

  const selectedResourcesDefaultValue =
    projectId || organizationId
      ? ({
          type: projectId ? "projects" : "organizations",
          id: projectId || organizationId!,
          name: projectId || organization?.name!
        } as const)
      : undefined;
  const [selectedResource, _setSelectedResource] = React.useState<
    | {
        type: "projects" | "organizations";
        id: string;
        name: string;
      }
    | undefined
  >(selectedResourcesDefaultValue);

  const submitIdeaQueryParameters = new URLSearchParams({
    category: "feature_request_cat",
    priority: "low",
    subject: intl.formatMessage({ id: "submit_an_idea.subject" }),
    description: intl.formatMessage({ id: "submit_an_idea.message" }),
    isGeneral: "true"
  });
  const submitIdeaUrl = getUrl({
    key: "users.tickets.open",
    props: { username: "-" }
  });

  const setSelectedResource = useCallback(
    value => {
      setInputValue("");
      _setSelectedResource(value);
    },
    [setInputValue]
  );

  React.useEffect(() => {
    inputRef?.current?.focus();
  }, [setInputValue]);

  return (
    <S.Container>
      <div className="command-k">
        <Command data-pendo-identifier="global.search.modal" loop>
          <S.CloseButton onClick={() => setIsCommandMenuOpen(false)} />
          {selectedResource?.id && (
            <S.Badge>{`${intl.formatMessage({ id: selectedResourceLabelMap[selectedResource.type] })}: ${selectedResource.type === "organizations" ? selectedResource.name : selectedResource.id}`}</S.Badge>
          )}
          <Command.Input
            autoFocus
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            data-pendo-identifier="global.search.input"
            placeholder={intl.formatMessage({
              id: "command_k.search.placeholder"
            })}
          />
          <S.Divider />
          <Command.List>
            {selectedResource ? (
              <SelectedResourceList
                selectedResource={selectedResource}
                search={inputValue}
                onListEmpty={() => _setSelectedResource(undefined)}
              />
            ) : (
              <ResourcesList setSelectedResource={setSelectedResource} />
            )}
          </Command.List>
          <S.Footer>
            <Button
              to={`${submitIdeaUrl}?${submitIdeaQueryParameters.toString()}`}
              variant="link"
              tabIndex={-1}
            >
              {intl.formatMessage({ id: "command_k.provide_feedback" })}
            </Button>
            <S.FooterBackWrapper>
              {intl.formatMessage({ id: "command_k.back" })}
              <S.KBD>{intl.formatMessage({ id: "command_k.esc" })}</S.KBD>
            </S.FooterBackWrapper>
          </S.Footer>
        </Command>
      </div>
    </S.Container>
  );
}

export function CommandMenu() {
  const { isCommandMenuOpen, setIsCommandMenuOpen } = useCommandMenuContext();
  const isCommandMenuEnabled = useAppSelector(selectCommandMenuEnabled);

  React.useEffect(() => {
    const down = e => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsCommandMenuOpen(isCommandMenuOpen => !isCommandMenuOpen);
      }

      if (e.key === "Escape") {
        e.preventDefault();
        setIsCommandMenuOpen(false);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [isCommandMenuOpen, setIsCommandMenuOpen]);

  if (!isCommandMenuEnabled || !isCommandMenuOpen) return;

  return <CommandK />;
}
